/** Delay (ms) before auto-advancing after feedback (multi-question quizzes + lesson steps). */
export const ENGINE_AUTO_ADVANCE_MS = 2200;

/** Shared layout tokens for activity engines (uses app theme CSS variables). */
export const engineShell = {
  width: '100%',
  maxWidth: 480,
  margin: '0 auto',
  padding: '16px 14px 20px',
  borderRadius: 20,
  background: 'var(--color-bg-card)',
  border: '2px solid var(--color-border)',
  boxShadow: '0 6px 24px var(--color-shadow)',
  boxSizing: 'border-box',
};

/**
 * Flat shell inside LessonPage card — one strong border on the lesson card only.
 * @see getEngineRootStyle
 */
export const engineShellGuided = {
  width: '100%',
  maxWidth: '100%',
  margin: 0,
  padding: '4px 0 12px',
  borderRadius: 0,
  background: 'transparent',
  border: 'none',
  boxShadow: 'none',
  boxSizing: 'border-box',
};

/**
 * @param {{ singleSurface?: boolean, maxWidth?: number | string }} [opts]
 * @returns {Record<string, unknown>}
 */
export function getEngineRootStyle(opts = {}) {
  const { singleSurface, maxWidth, ...rest } = opts;
  const base = singleSurface ? { ...engineShellGuided } : { ...engineShell };
  if (maxWidth != null) base.maxWidth = maxWidth;
  return { ...base, ...rest };
}

export const titleStyle = {
  margin: '0 0 8px',
  fontSize: 'clamp(1.15rem, 3.5vw, 1.35rem)',
  fontWeight: 800,
  color: 'var(--color-text)',
  lineHeight: 1.25,
};

export const instructionsStyle = {
  margin: '0 0 16px',
  fontSize: 16,
  lineHeight: 1.45,
  color: 'var(--color-text)',
  opacity: 0.85,
  fontWeight: 500,
};

export const primaryBtn = {
  minHeight: 48,
  padding: '12px 18px',
  borderRadius: 14,
  border: 'none',
  fontWeight: 700,
  fontSize: 16,
  cursor: 'pointer',
  background: 'var(--color-primary)',
  color: '#fff',
  width: '100%',
  maxWidth: '100%',
};

export const secondaryBtn = {
  ...primaryBtn,
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
  border: '2px solid var(--color-border)',
};

/** Large tap targets for guided rounds — neutral until selected (not solid primary). */
export const tapRoundNeutralChoiceBtn = {
  minHeight: 56,
  padding: '14px 16px',
  borderRadius: 14,
  border: '2px solid var(--color-border)',
  fontWeight: 700,
  fontSize: 16,
  cursor: 'pointer',
  background: 'var(--color-bg-card)',
  color: 'var(--color-text)',
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
};

export const hintBtn = {
  minHeight: 44,
  padding: '8px 14px',
  borderRadius: 12,
  border: '2px dashed var(--color-primary)',
  background: 'transparent',
  color: 'var(--color-primary)',
  fontWeight: 700,
  fontSize: 14,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
};

export const feedbackCorrect = {
  padding: '12px 14px',
  borderRadius: 12,
  background: 'rgba(34, 197, 94, 0.15)',
  border: '1px solid rgba(34, 197, 94, 0.4)',
  color: 'var(--color-text)',
  fontWeight: 600,
  fontSize: 15,
};

export const feedbackIncorrect = {
  ...feedbackCorrect,
  background: 'rgba(239, 68, 68, 0.12)',
  border: '1px solid rgba(239, 68, 68, 0.35)',
};

/** Friendly “try again” styling for kids — warm amber, not alarming red */
export const feedbackWarmHint = {
  padding: '10px 12px',
  borderRadius: 12,
  background: 'rgba(245, 158, 11, 0.14)',
  border: '1px solid rgba(217, 119, 6, 0.42)',
  color: 'var(--color-text)',
  fontWeight: 600,
  fontSize: 14,
  lineHeight: 1.45,
};
